import { getColor, getSize } from "views/resources/theme";
import Icon, { Icons } from "react-hp-icons";
import React, { useState } from "react";
import styled from "styled-components";
import { flip, curry } from "lib/functions";
import { addDate, isBefore } from "lib/date";

const oneDay = 24 * 60 * 60 * 1000;
const oneWeek = 7 * oneDay;
const dayAbbrevs = ["dom.", "lun.", "mar.", "miér.", "jue.", "vier.", "sáb."];
const monthAbbrevs = [
	"enero",
	"febrero",
	"marzo",
	"abril",
	"mayo",
	"junio",
	"julio",
	"agosto",
	"septiembre",
	"octubre",
	"noviembre",
	"diciembre",
];

const isEqualTo = (date0) => (date1) =>
	date0.getTime() - date1.getTime() < oneDay * 2 &&
	date0.getDay() === date1.getDay();

const dateAdd = curry(addDate);

export const Calendar = ({ onDayClick, maxFutureDays, excludedDays = [] }) => {
	// FIXME: this date should be in the office timezone, not in the browser's
	// Otherwise, getDate() and getMonth() do not work properly
	const [date, setDate] = useState(new Date()); // date shuould be named initialDate
	const [selectedDate, setSelectedDate] = useState(date);
	const dateLimit = new Date(new Date().getTime() + maxFutureDays * oneDay);
	const goForwardOneWeek = () =>
		isBefore(date, addDate(dateLimit, oneDay * -7))
			? setDate(flip(dateAdd)(oneWeek))
			: null;
	const goBackOneWeek = () => setDate(flip(dateAdd)(oneWeek * -1));

	const onDayClickWrapper = (date) => (ev) => {
		onDayClick(date);
		setSelectedDate(date);
	};

	const month = monthAbbrevs[date.getMonth()];
	const year = date.getFullYear();
	const days = [0, 1, 2, 3, 4, 5, 6]
		.map((i) => i * oneDay)
		.map(dateAdd(date))
		.filter((date) => isBefore(date, dateLimit))
		.map((d) => {
			const day = {
				dayName: dayAbbrevs[d.getDay()],
				dayNumber: d.getDate(),
				isSelected: d.getTime() === selectedDate.getTime(),
				onClick: onDayClickWrapper(d),
			};
			if (excludedDays.some(isEqualTo(d))) {
				day.onClick = () => {};
				day.excluded = true;
				return day;
			}
			return day;
		});

	return (
		<>
			<Week
				month={month}
				year={year}
				days={days}
				onLeftArrowClick={goBackOneWeek}
				onRightArrowClick={goForwardOneWeek}
			/>
		</>
	);
};

const Week = ({ month, year, days, onLeftArrowClick, onRightArrowClick }) => {
	return (
		<Wrapper>
			<RowDate>
				<Arrow onClick={onLeftArrowClick}>
					<Icon type={Icons.left} size={1} />
				</Arrow>
				<Dates>
					{month}, {year}
				</Dates>
				<Arrow onClick={onRightArrowClick}>
					<Icon type={Icons.right} size={1} />
				</Arrow>
			</RowDate>
			<Row>
				{days.map((d) => (
					<Day {...d} />
				))}
			</Row>
		</Wrapper>
	);
};

const Day = ({ dayName, dayNumber, onClick, isSelected, excluded }) => (
	<Column>
		<div>{dayName}</div>
		<SDate onClick={onClick} isSelected={isSelected} excluded={excluded}>
			<label>{dayNumber}</label>
		</SDate>
	</Column>
);

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	background-color: ${getColor("bg")};
`;

const Column = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const Row = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 0 ${getSize("m")};
	margin-bottom: 8px;
	align-items: center;
`;

const isPropOrElse = (propName) => (thenValue, elseValue) => (props) =>
	props[propName] ? thenValue : elseValue;
const isSelected = isPropOrElse("isSelected");

const SDate = styled.div`
	display: inline-block;
	color: ${isSelected("#fff", "inherit")};
	background-color: ${isSelected("#7d3087", "transparent")};
	${(p) => p.excluded && "background-color:grey;"}
	width: 28px;
	height: 28px;
	margin: 10px;
	line-height: 28px;
	border-radius: 50%;
	text-align: center;
	font-size: 16px;
	cursor: pointer;
	label {
		cursor: pointer;
	}
`;

const RowDate = styled.div`
	margin: 0px 0px 20px;
`;

const Arrow = styled.span`
	display: inline-block;
	margin: 0px 5px;
`;

const Dates = styled.span`
	display: inline-block;
`;
